import { isNumber } from "lodash";
import {
  RADAR_GRID_OBJECT_KIND,
  RG_RISK_LEVEL,
  RADAR_GRID_OBJECT_RISK_LEVEL,
  RG_RISK_LEVEL_THRESHOLD,
} from "../consts/radarGridLayerConstants";

export function createRiskEvaluator({
  r,
  ai_r: aiRiskLevel,
  man_r: manRiskLevel,
  k: kind,
  cog,
} = {}) {
  const hasManualAssessment = () => isValidRiskLevel(manRiskLevel);

  const hasAiAssessment = () => isValidRiskLevel(aiRiskLevel);

  const calcAutoRiskLevel = () => {
    if (hasAiAssessment()) return parseRiskLevelByThreshold(aiRiskLevel);
    return RG_RISK_LEVEL.SAFE;
  };

  const calcRiskLevel = () => {
    if (isValidRiskLevel(r)) return parseRiskLevelByThreshold(r);
    if (isValidRiskLevel(manRiskLevel))
      return parseRiskLevelByThreshold(manRiskLevel);
    if (isValidRiskLevel(aiRiskLevel))
      return parseRiskLevelByThreshold(aiRiskLevel);
    return RG_RISK_LEVEL.SAFE;
  };

  const isValidCog = () => isNumber(cog) && cog >= 0 && cog <= 360;

  const isShip = () => kind === RADAR_GRID_OBJECT_KIND.SHIP;

  const kindToIconType = kind => {
    if (kind === RADAR_GRID_OBJECT_KIND.BUOY) return "buoy";
    if (kind === RADAR_GRID_OBJECT_KIND.FLOATING) return "floating";
    return "vessel";
  };

  const calcColor = () => {
    return RADAR_GRID_OBJECT_RISK_LEVEL[calcRiskLevel()].color;
  };

  const calcTrackIcon = () => {
    const iconType = kindToIconType(kind);
    const riskLabel = RADAR_GRID_OBJECT_RISK_LEVEL[calcRiskLevel()].label;
    if (iconType === "buoy" || iconType === "floating") {
      return `rg-target-object-${riskLabel}-default`;
    }

    if (isValidCog()) return `rg-${iconType}-${riskLabel}`;
    return `rg-${iconType}-none-${riskLabel}`;
  };

  const calcTargetIcon = () => {
    const riskLabel = RADAR_GRID_OBJECT_RISK_LEVEL[calcRiskLevel()].label;
    return hasManualAssessment()
      ? `rg-target-object-manual-${riskLabel}`
      : `rg-target-object-${riskLabel}-default`;
  };

  const calcObjectIcon = () => {
    return _isKindValid(kind) ? calcTrackIcon() : calcTargetIcon();
  };

  const calcFrameIconId = () => {
    if (
      kind === RADAR_GRID_OBJECT_KIND.BUOY ||
      kind === RADAR_GRID_OBJECT_KIND.FLOATING ||
      hasManualAssessment()
    )
      return "";
    return _getFrameIconId(calcRiskLevel(), _isKindValid(kind));
  };

  const calcBrinkIconId = () => {
    if (kind === RADAR_GRID_OBJECT_KIND.BUOY) return "";
    return _getBrinkIconId(calcRiskLevel(), _isKindValid(kind));
  };

  const isValidRiskLevel = value =>
    typeof value === "number" && (value === 99 || (value >= 0 && value <= 10));

  const parseRiskLevelByThreshold = riskLevel => {
    const { SAFE, CAUTION, DANGER } = RG_RISK_LEVEL;
    if (riskLevel === 99) return SAFE;
    if (!isValidRiskLevel(riskLevel)) return null;
    if (riskLevel < RG_RISK_LEVEL_THRESHOLD[CAUTION]) return SAFE;
    if (riskLevel < RG_RISK_LEVEL_THRESHOLD[DANGER]) return CAUTION;
    return DANGER;
  };

  const _isKindValid = objectKind => {
    return new Set(Object.values(RADAR_GRID_OBJECT_KIND)).has(objectKind);
  };

  const _getFrameIconId = (riskLevel, isKindValid = true) => {
    const riskLevelObject = RADAR_GRID_OBJECT_RISK_LEVEL[riskLevel];
    if (isKindValid) return riskLevelObject.frameIcon;
    return null;
  };

  const _getBrinkIconId = (riskLevel, isKindValid = true) => {
    const riskLevelObject = RADAR_GRID_OBJECT_RISK_LEVEL[riskLevel];
    if (isKindValid) return riskLevelObject.brinkIconId;
    return riskLevelObject.brinkOtherKindIconId;
  };

  return {
    color: calcColor(),
    objectIcon: calcObjectIcon(),
    frameIcon: calcFrameIconId(),
    brinkIconId: calcBrinkIconId(),
    hasManualAssessment: hasManualAssessment(),
    autoRiskLevel: calcAutoRiskLevel(),
    riskLevel: calcRiskLevel(),
    isValidCog: isValidCog(),
    isShip: isShip(),
  };
}
